/**
 * Admin → user memberships across tenants. Powers the inline picker on
 * the customer detail page where a reseller grants/revokes a user's
 * access to other workspaces in the subtree.
 *
 * Distinct from useMemberships (which is the SELF endpoint that returns
 * the logged-in user's own memberships for the topbar switcher). This
 * hook hits the admin endpoints and is reseller-scoped on the server.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type MembershipRole = 'owner' | 'admin' | 'operator' | 'viewer';

export interface AdminMembership {
  id: number;
  tenant_id: number;
  tenant_name: string;
  tenant_type: 'platform' | 'reseller' | 'customer';
  role: MembershipRole;
  created_at: string;
}

export interface AdminMembershipsResponse {
  items: AdminMembership[];
}

export interface AssignableTenant {
  id: number;
  name: string;
  type: 'platform' | 'reseller' | 'customer';
}

export interface AssignableTenantsResponse {
  items: AssignableTenant[];
}

const userKey = (userId: number) =>
  ['admin', 'users', userId, 'memberships'] as const;
const assignableKey = (userId: number) =>
  ['admin', 'users', userId, 'memberships', 'assignable'] as const;

export function useUserMemberships(userId: number | null) {
  return useQuery({
    queryKey: userId ? userKey(userId) : ['admin', 'users', 'noop', 'memberships'],
    queryFn: () =>
      api.get<AdminMembershipsResponse>(`/v1/admin/users/${userId}/memberships`),
    enabled: userId !== null && userId > 0,
    staleTime: 30_000,
  });
}

export function useAssignableTenants(userId: number | null) {
  return useQuery({
    queryKey: userId ? assignableKey(userId) : ['admin', 'users', 'noop', 'assignable'],
    queryFn: () =>
      api.get<AssignableTenantsResponse>(
        `/v1/admin/users/${userId}/memberships/assignable`,
      ),
    enabled: userId !== null && userId > 0,
    staleTime: 30_000,
  });
}

export function useAddMembership(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, role }: { tenantId: number; role?: MembershipRole }) =>
      api.post<AdminMembership>(`/v1/admin/users/${userId}/memberships`, {
        tenant_id: tenantId,
        role: role ?? 'operator',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKey(userId) });
      void qc.invalidateQueries({ queryKey: assignableKey(userId) });
    },
  });
}

export function useRemoveMembership(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: number) =>
      api.delete<void>(`/v1/admin/users/${userId}/memberships/${tenantId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKey(userId) });
      void qc.invalidateQueries({ queryKey: assignableKey(userId) });
    },
  });
}
